const isWebAuthnSupported = () => window.PublicKeyCredential !== undefined;

const CHECKED_DIRECTIVES = ['publickey-credentials-get', 'publickey-credentials-create', 'payment'];

const checkPermissionsPolicy = () => {
  const policy = document.permissionsPolicy;
  const featurePolicy = document.featurePolicy;
  const result = {};

  CHECKED_DIRECTIVES.forEach((directive) => {
    try {
      if (policy && typeof policy.allowsFeature === 'function') {
        result[directive] = policy.allowsFeature(directive) ? 'AVAILABLE' : 'UNAVAILABLE';
      } else if (featurePolicy && typeof featurePolicy.allowsFeature === 'function') {
        result[directive] = featurePolicy.allowsFeature(directive) ? 'AVAILABLE' : 'UNAVAILABLE';
      } else {
        result[directive] = 'UNKNOWN';
      }
    } catch (error) {
      result[directive] = 'UNKNOWN';
    }
  });

  return result;
};

const isSPCSupported = (policyResult = checkPermissionsPolicy()) => {
  if (!isWebAuthnSupported()) return false;
  if (typeof window.PaymentRequest === 'undefined') return false;

  return CHECKED_DIRECTIVES.every((directive) => policyResult[directive] === 'AVAILABLE');
};

const gatherCapabilities = async () => {
  const PKC = window.PublicKeyCredential;
  const permissionsPolicy = checkPermissionsPolicy();

  if (!PKC) {
    return {
      extensions: {
        credProps: false,
        payment: false,
        browserBoundKeyHardware: false,
      },
      passkeyPlatformAuthenticator: false,
      permissionsPolicy,
      relatedOrigins: false,
      userVerifyingPlatformAuthenticator: false,
    };
  }

  const PR = typeof window.PaymentRequest !== 'undefined' ? window.PaymentRequest : null;
  const payment = PR && typeof PR.securePaymentConfirmationAvailability === 'function'
    ? await PR.securePaymentConfirmationAvailability()
      .then((value) => value === 'available')
      .catch(() => false)
    : false;

  const browserBoundKeyHardware = PR && typeof PR.getSecurePaymentConfirmationCapabilities === 'function'
    ? await PR.getSecurePaymentConfirmationCapabilities()
      .then((resp) => resp.browserBoundKeyHardware)
      .catch(() => false)
    : false;

  let passkeyPlatformAuthenticator = false;
  let relatedOrigins = false;
  let userVerifyingPlatformAuthenticator = false;

  if (typeof PKC.getClientCapabilities === 'function') {
    try {
      const caps = await PKC.getClientCapabilities();
      passkeyPlatformAuthenticator = caps.passkeyPlatformAuthenticator || false;
      relatedOrigins = caps.relatedOrigins || false;
      userVerifyingPlatformAuthenticator = caps.userVerifyingPlatformAuthenticator || false;
    } catch (error) {
      userVerifyingPlatformAuthenticator = await PKC
        .isUserVerifyingPlatformAuthenticatorAvailable()
        .catch(() => false);
      passkeyPlatformAuthenticator = userVerifyingPlatformAuthenticator;
    }
  } else {
    userVerifyingPlatformAuthenticator = await PKC
      .isUserVerifyingPlatformAuthenticatorAvailable()
      .catch(() => false);
    passkeyPlatformAuthenticator = userVerifyingPlatformAuthenticator;
  }

  return {
    extensions: {
      credProps: true,
      payment,
      browserBoundKeyHardware,
    },
    passkeyPlatformAuthenticator,
    permissionsPolicy,
    relatedOrigins,
    userVerifyingPlatformAuthenticator,
  };
};

const events = {
  webAuthnSupported: {
    eventCd: 'webauthn',
    meta: {
      screenId: 'wait_screen',
      detail: 'supported',
    },
  },
  webAuthnNotSupported: {
    eventCd: 'webauthn',
    meta: {
      screenId: 'wait_screen',
      detail: 'not_supported',
    },
  },
  platformAuthnAvailable: {
    eventCd: 'webauthn',
    meta: {
      screenId: 'wait_screen',
      elementId: 'platform_authenticator',
      detail: 'available',
    },
  },
  platformAuthnNotAvailable: {
    eventCd: 'webauthn',
    meta: {
      screenId: 'wait_screen',
      elementId: 'platform_authenticator',
      detail: 'unavailable',
    },
  },
};

const logs = {
  publicKeyCredentialError: 'PUBLIC_KEY_CREDENTIAL_ERROR',
};

export {
  isWebAuthnSupported,
  checkPermissionsPolicy,
  isSPCSupported,
  gatherCapabilities,
  CHECKED_DIRECTIVES,
  events,
  logs,
};
